import { Error, ValueOf, RestaurantBranch } from 'lib/types/global';

export const OfferKind = {
  ITEM: 'percentage_off_on_items',
  BASKET: 'percentage_off_on_basket',
  FREE_DELIVERY: 'free_delivery',
} as const;
export type OfferKindValue = ValueOf<typeof OfferKind>;

export const OfferTheme = {
  BASKET_FLASH_DEALS: 'basket-flash-deals',
  FLASH_DEALS: 'flash-deals',
  FREE_ITEMS: 'free-items',
  GROUPS: 'groups',
  RESTAURANT_PICKS: 'restaurant-picks',
} as const;
export type OfferThemeValue = ValueOf<typeof OfferTheme>;

export const Status = {
  SCHEDULED: 'SCHEDULED',
  LIVE: 'LIVE',
  ENDED: 'ENDED',
  CANCELLED: 'CANCELLED',
} as const;
export type StatusValue = ValueOf<typeof Status>;

export const Audience = {
  EVERYONE: 'orders',
  PLUS: 'subscribers',
  NEW_USERS: 'new_customers',
} as const;
export type AudienceValue = ValueOf<typeof Audience>;

export const FulfillmentMethod = {
  COLLECTION: 'collection',
  DELIVERY: 'delivery',
  DINE_IN: 'dine_in',
} as const;
export type FulfillmentMethodValue = ValueOf<typeof FulfillmentMethod>;

export const ScheduleType = {
  ANOTHER_DAY: 'another_day',
  LATER: 'later',
  NOW: 'now',
  RECURRING: 'recurring',
  TODAY: 'today',
} as const;
export type ScheduleTypeValue = ValueOf<typeof ScheduleType>;

export const Source = {
  BULK_OFFER: 'deliveroo',
  MARKETER: 'marketer',
} as const;
export type SourceValue = ValueOf<typeof Source>;

export const Weekday = {
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
  7: 'sunday',
} as const;
export type WeekdayKey = keyof typeof Weekday;

// Item offer types
export type Inventory = {
  menus: Menu[];
  categories: Category[];
  items: Item[];
};

export type Menu = {
  id: number;
  name: string;
  categoryIds: number[];
  image: string | null;
  branches: (RestaurantBranch | undefined)[];
};

export type Category = {
  id: number;
  name: string;
  description: string;
  itemIds: number[];
};

export type Item = {
  id: number;
  uniqueId: number;
  name: string;
  description: string;
  price: {
    value: number;
    currencyCode: string;
  };
  available: boolean;
  alcohol: boolean;
  categories: Category[];
  menu: Partial<Menu>;
};

export type Offer = {
  daysOfWeek: string | null;
  discount: number;
  endAt: string;
  fulfillmentMethod: FulfillmentMethodValue | null;
  fulfillmentMethods: FulfillmentMethodValue[];
  goal: AudienceValue;
  id: number;
  mov: number;
  movDecimal: number;
  offerStatus: StatusValue;
  orgId: number;
  restaurantIds: string[];
  rewardType: OfferKindValue;
  startAt: string;
  theme: string;
  timeOfDayFrom: string | null;
  timeOfDayUntil: string | null;
  timezone: string;
  source: SourceValue;
  startDate: string;
};

export type Price = {
  label: string;
  value: number;
};

export type BasketOfferConfig = {
  type: typeof OfferKind.BASKET;
  movValues: number[];
  discountValues: number[];
  themes: {
    name: OfferThemeValue;
    movValues: number[];
    discountValues: number[];
    supportedFulfillmentMethods: FulfillmentMethodValue[];
    supportedScheduleTypes: ScheduleTypeValue[];
    cancellable: boolean;
  }[];
};

export type ItemOfferConfig = {
  type: typeof OfferKind.ITEM;
  themes: {
    name: string;
    minimumItemPrice: number;
    movValues: number[];
    discountValues: number[];
  }[];
  lowestItemPrice: number;
};

export type ValueWithAvailability<V> = {
  name: V;
  available: boolean;
};

export type Config = {
  country: string;
  countryHasPlus: boolean;
  currency: string;
  discountValues: Price[];
  fulfillmentMethods: FulfillmentMethodValue[];
  goals: ValueWithAvailability<AudienceValue>[];
  movValues: Price[];
  newOfferRewardType: OfferKindValue;
  offerTypes: [BasketOfferConfig, ItemOfferConfig];
  timezone: string;
};

export type NewOffer = Partial<
  {
    daysOfWeek: WeekdayKey[] | null;
    discount: number;
    mov: number;
    menuItems?: number[];
    rewardType: OfferKindValue;
    scheduleType: ScheduleTypeValue;
    theme: OfferThemeValue;
    type: OfferKindValue;
    validate?: boolean;
    fulfillmentMethods: FulfillmentMethodValue[];
    startTime: string;
    endTime: string;
  } & Pick<
    Offer,
    | 'orgId'
    | 'goal'
    | 'mov'
    | 'startAt'
    | 'endAt'
    | 'restaurantIds'
    | 'timeOfDayFrom'
    | 'timeOfDayUntil'
    | 'timezone'
  >
>;

export type OfferCampaign = {
  cancelledAt?: string;
  drnId: string;
  name: string;
  startAt: string;
  endAt: string;
};

// Order is a simplified version of the order data.
export type Order = {
  id: number;
  subtotal: string;
  placedAt: string;
};

export type OffersState = {
  campaign?: OfferCampaign;
  campaignRedemption?: {
    totalDiscount: string;
    totalOrders: number;
  };
  campaignOrders?: Order[];
  existingOffers: Offer['id'][];
  existingOffersById: Record<number, Offer>;
  newOffer: NewOffer | undefined;
  paginatedOffers: {
    metadata: {
      pagination: {
        hasNextPage?: boolean;
        page: number;
        resultsPerPage: number;
      };
      status?: string;
      fulfillmentMethod?: FulfillmentMethodValue;
      restaurantIds?: number[];
      totalCount?: number;
    };
    offers: Offer[];
  };
  inventory: Inventory;
  isLoading?: boolean;
  error?: Pick<Error, 'status'>;
  campaignError?: Pick<Error, 'status'>;
} & Partial<Config>;
